// PRC-026: estado de la cuenta de Pricing recordado en el navegador para pintar
// la aplicación al instante al recargar. Solo estado de la cuenta (rol, plan,
// acceso y fechas): nunca datos de negocio ni el token. El servidor lo
// confirma en segundo plano y comprueba el acceso en cada petición.
import type { Tier } from '../../types';

export const PRICING_MEMORY_KEY = 'proa-pricing-account-state-v1';

export interface RememberedPricingState {
  userId: string;
  role: 'admin' | 'member';
  access: {
    state: 'trial' | 'paid' | 'expired' | 'unavailable';
    has_access: boolean;
    trial_started_at: string | null;
    trial_ends_at: string | null;
    trial_optimizations_used: number;
    trial_optimization_limit?: number;
  };
  tier: Tier | null;
  profile: { tier_id?: string | null; full_name?: string | null; company_name?: string | null };
  accountName: string | null;
}

export function readPricingMemory(userId: string): RememberedPricingState | null {
  try {
    const raw = window.localStorage.getItem(PRICING_MEMORY_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as RememberedPricingState;
    // Solo del mismo usuario y con acceso: «sin acceso» siempre espera al servidor.
    if (value?.userId !== userId || value.access?.has_access !== true) return null;
    if (value.role !== 'admin' && value.role !== 'member') return null;
    // Una prueba ya vencida cuenta como «sin acceso».
    if (value.access.state === 'trial' && (!value.access.trial_ends_at || new Date(value.access.trial_ends_at).getTime() <= Date.now())) return null;
    return value;
  } catch {
    return null;
  }
}

export function savePricingMemory(value: RememberedPricingState): void {
  try {
    if (!value.access.has_access) {
      window.localStorage.removeItem(PRICING_MEMORY_KEY);
      return;
    }
    window.localStorage.setItem(PRICING_MEMORY_KEY, JSON.stringify(value));
  } catch {
    // Sin almacenamiento: se carga del servidor como siempre.
  }
}

export function clearPricingMemory(): void {
  try {
    window.localStorage.removeItem(PRICING_MEMORY_KEY);
  } catch {
    // Nada que borrar.
  }
}

export const hasPricingMemory = (): boolean => {
  try {
    return !!window.localStorage.getItem(PRICING_MEMORY_KEY);
  } catch {
    return false;
  }
};
